import get from 'lodash/get'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import uniq from 'lodash/uniq'

import {reducer} from '../helpers'
import {
  formatShippingAddress,
  getUniqueIdForItem,
  isCurrentItemToExchangeTailoringOnly,
  shouldNavigateToAddressForm,
} from './helpers'

import {
  GET_ORDER_ITEMS_TO_EXCHANGE_FAILURE,
  GET_ORDER_ITEMS_TO_EXCHANGE_PENDING,
  GET_ORDER_ITEMS_TO_EXCHANGE_SUCCESS,
} from '../../actions/order'
import {
  EDIT_ISSUE,
  END_REPLACEMENTS,
  GET_PRODUCT_SIZES_FOR_EXCHANGE_FAILURE,
  GET_PRODUCT_SIZES_FOR_EXCHANGE_PENDING,
  GET_PRODUCT_SIZES_FOR_EXCHANGE_SUCCESS,
  GET_SHIPPING_ADDRESS_FOR_EXCHANGE_FAILURE,
  GET_SHIPPING_ADDRESS_FOR_EXCHANGE_PENDING,
  GET_SHIPPING_ADDRESS_FOR_EXCHANGE_SUCCESS,
  SET_FIT_DATA,
  START_REPLACEMENTS,
  SUBMIT_EXCHANGE_REQUEST,
  SUBMIT_EXCHANGE_REQUEST_FAILURE,
  SUBMIT_EXCHANGE_REQUEST_PENDING,
  SUBMIT_EXCHANGE_REQUEST_SUCCESS,
  SUBMIT_FIT_QUESTIONNAIRE,
  SUBMIT_SELECT_ISSUES,
  SUBMIT_SELECT_ITEMS,
  SUBMIT_SUMMARY,
  SUBMIT_TAILORING_CONTINUE_EXCHANGE,
  SUBMIT_TAILORING_NO_EXCHANGE,
  TOGGLE_COLLAR_EXTENDER_NEEDED,
} from '../../actions/replacements'
import * as replacementsSelectors from '../../store/replacements'
import {
  REPLACEMENTS_FIT_ISSUE,
  ReplacementsItemIssueOptions,
  ReplacementsSteps,
} from '../../store/replacements/enums'

export const initialState = {
  currentItemToExchangeIndex: 0,
  currentItemIssueIndex: 0,
  currentEventId: null,
  currentOrderId: null,
  currentStep: ReplacementsSteps.itemSelect,
  eligibleItemsToExchange: {
    isLoading: false,
    error: false,
    allIds: [],
    byId: {},
  },
  selectedFitData: {},
  isEditing: false,
  selectedItemsToExchange: {
    allIds: [],
    byId: {},
  },
  productSizes: {
    isLoading: false,
    isFetched: false,
    allIds: [],
    byId: {},
  },
  submitExchangeRequest: {
    error: false,
    isPending: false,
  },
  shippingInfo: {
    isAuthorizedToCharge: false,
    isFetchingShippingAddress: false,
    shippingAddress: null,
    shippingMethod: null,
    travelingAddress: null,
    travelDate: null,
  },
}

/**
 * This helper function calculates the next step and item index
 * after user submits answers for the fit questionnaire step.
 *
 * @param state
 * @returns {{currentStep: *, currentItemToExchangeIndex: (number|*)}}
 */
export function reduceCurrentStepAndItemIndex(state) {
  const result = {...state}

  // Navigate to the summary page if user has reached the last item,
  // otherwise, navigate to the issue select step for the next item
  if (
    state.currentItemToExchangeIndex ===
    replacementsSelectors.allSelectedItemsToExchangeIds({replacements: state}).length - 1
  ) {
    result.currentStep = ReplacementsSteps.summary
  } else {
    result.currentStep = ReplacementsSteps.issueSelect
    result.currentItemToExchangeIndex += 1
  }

  return result
}

export default reducer(initialState, (state, action) => {
  const {payload, type} = action

  switch (type) {
    case START_REPLACEMENTS:
      return {
        currentEventId: payload.eventId,
        currentOrderId: payload.orderId,
      }

    case END_REPLACEMENTS:
      return {...initialState}

    case EDIT_ISSUE:
      return {
        isEditing: true,
        currentItemToExchangeIndex: state.selectedItemsToExchange.allIds.indexOf(payload.itemId),
        currentStep: payload.currentStep,
      }

    /*
     * GET order items API reducer logic
     */
    case GET_ORDER_ITEMS_TO_EXCHANGE_PENDING:
      return {
        eligibleItemsToExchange: {
          ...state.eligibleItemsToExchange,
          error: false,
          isLoading: true,
        },
      }
    case GET_ORDER_ITEMS_TO_EXCHANGE_FAILURE:
      return {
        eligibleItemsToExchange: {
          ...state.eligibleItemsToExchange,
          error: true,
          isLoading: false,
        },
      }
    case GET_ORDER_ITEMS_TO_EXCHANGE_SUCCESS: {
      // Socks are not eligible for replacements
      const modifiedPayload = payload.filter((product) => product.categorySlug !== 'socks')

      return {
        eligibleItemsToExchange: {
          error: false,
          isLoading: false,
          allIds: modifiedPayload.map(getUniqueIdForItem),
          byId: modifiedPayload.reduce((byId, item) => {
            const id = getUniqueIdForItem(item)
            return {...byId, [id]: {...item, id}}
          }, {}),
        },
      }
    }
    /*
     * GET product sizes for item API reducer logic
     */
    case GET_PRODUCT_SIZES_FOR_EXCHANGE_PENDING:
      return {
        productSizes: {
          ...state.productSizes,
          isLoading: true,
        },
      }
    case GET_PRODUCT_SIZES_FOR_EXCHANGE_FAILURE:
      return {
        productSizes: {
          ...state.productSizes,
          isLoading: false,
        },
      }
    case GET_PRODUCT_SIZES_FOR_EXCHANGE_SUCCESS:
      return merge({}, state, {
        productSizes: {
          isFetched: true,
          isLoading: false,
          allIds: uniq([...state.productSizes.allIds, payload.id]),
          byId: {
            ...state.productSizes.byId,
            [payload.id]: payload,
          },
        },
      })

    /*
     * Exchange flow navigation reducer logic
     */
    case SUBMIT_SELECT_ITEMS:
      return {
        currentStep: ReplacementsSteps.issueSelect,
        selectedItemsToExchange: {
          allIds: payload,
          byId: payload.reduce((byId, id) => {
            const itemToExchangeAtId = state.selectedItemsToExchange.byId[id]
            return {
              ...byId,
              [id]: {
                ...itemToExchangeAtId,
                issues: get(itemToExchangeAtId, 'issues', []),
              },
            }
          }, {}),
        },
      }
    case SUBMIT_SELECT_ISSUES: {
      // check to see if user reported a fit issue with current item
      const userSelectedFitIssue = payload.selectedIssuesIndices.includes(
        ReplacementsItemIssueOptions.indexOf(REPLACEMENTS_FIT_ISSUE),
      )
      const selectedItemId = state.selectedItemsToExchange.allIds[state.currentItemToExchangeIndex]

      const getCurrentStep = () => {
        // check if current item category is eligible for reporting fit issue
        const isCurrentItemEligibleForFitQuestionnaire =
          replacementsSelectors.isCurrentItemEligibleForFitQuestionnaire({replacements: state})
        const hasFitQuestionAnswers =
          state.selectedItemsToExchange.byId[selectedItemId]?.issuesByType?.fit?.fitQuestionAnswers
            .length > 0

        // If there are already fit answers, set to summary
        if (hasFitQuestionAnswers) {
          return ReplacementsSteps.summary
        }

        // If fit issue selected, set to fit step, otherwise set to summary step
        return userSelectedFitIssue && isCurrentItemEligibleForFitQuestionnaire
          ? ReplacementsSteps.fitIssue
          : ReplacementsSteps.summary
      }

      const getIssuesByType = () => {
        // If fit issue is the only selection, include only fit
        if (userSelectedFitIssue && payload.selectedIssuesIndices.length === 1) {
          return {...state.selectedItemsToExchange.byId[selectedItemId].issuesByType}
        }

        // If both fit and general issues are chosen, include both
        if (userSelectedFitIssue && payload.selectedIssuesIndices.length > 1) {
          return {
            ...state.selectedItemsToExchange.byId[selectedItemId].issuesByType,
            general: {
              itemToExchange: payload.itemToExchange,
            },
          }
        }

        // If general issue is the only selection, include only general
        return {
          general: {
            itemToExchange: payload.itemToExchange,
          },
        }
      }

      const updatedState = {
        ...state,
        currentStep: getCurrentStep(),
        selectedItemsToExchange: {
          ...state.selectedItemsToExchange,
          byId: {
            ...state.selectedItemsToExchange.byId,
            [selectedItemId]: {
              ...state.selectedItemsToExchange.byId[selectedItemId],
              issues: payload.selectedIssuesIndices,
              issuesByType: getIssuesByType(),
            },
          },
        },
        isEditing: false,
        productSizes: {
          ...state.productSizes,
          // this ensures that subsequent fetches of a product size will not have a flash of the rendered content
          isFetched: false,
        },
      }

      // If nextStep is fitIssue go to fit step
      if (getCurrentStep() === ReplacementsSteps.fitIssue) {
        return updatedState
      }

      // If not editing and there are multiple items selected, go to next item
      if (!state.isEditing && Object.keys(state.selectedItemsToExchange.allIds).length > 1) {
        return reduceCurrentStepAndItemIndex(updatedState)
      }

      // Go to next step
      return updatedState
    }

    case SET_FIT_DATA:
      return {
        selectedFitData: {
          fitOption: payload.fitOption,
          metaDataType: payload.metaDataType,
        },
      }

    case SUBMIT_FIT_QUESTIONNAIRE: {
      const selectedItemId = state.selectedItemsToExchange.allIds[state.currentItemToExchangeIndex]
      const stateWithAnswers = merge({}, state, {
        selectedItemsToExchange: {
          byId: {
            [selectedItemId]: {issuesByType: {fit: payload}},
          },
        },
      })

      // check if item conditions are eligible for tailoring only flow intercept
      if (isCurrentItemToExchangeTailoringOnly(stateWithAnswers)) {
        return {
          // reset tailoring flag before navigating to the tailoring intercept
          ...omit(
            stateWithAnswers,
            `selectedItemsToExchange.byId[${selectedItemId}].issuesByType.fit.isTakingToTailor`,
          ),
          currentStep: ReplacementsSteps.tailoringOnly,
        }
      }

      /**  navigate back to summary step if we're in edit mode or
       * if user reported additional non-fit issues for current item and
       * there is only one item selected
       */
      if (
        state.isEditing ||
        (replacementsSelectors.currentItemHasNonFitIssues({replacements: state}) &&
          state.selectedItemsToExchange.allIds.length === 1)
      ) {
        return {...stateWithAnswers, currentStep: ReplacementsSteps.summary, isEditing: false}
      }

      return reduceCurrentStepAndItemIndex(stateWithAnswers)
    }

    // TODO: in the following phase, we will need to add additional logic to submit a Zendesk ticket
    // for this use case since currently we kick the user out of the replacement flow at this point
    case SUBMIT_TAILORING_NO_EXCHANGE: {
      // add tailoring flag to current item to exchange
      const selectedItemId = state.selectedItemsToExchange.allIds[state.currentItemToExchangeIndex]
      const stateWithTailoringFlag = merge({}, state, {
        selectedItemsToExchange: {
          byId: {
            [selectedItemId]: {
              issuesByType: {
                fit: {isTakingToTailor: true},
              },
            },
          },
        },
      })

      // navigate to tailoring end step to end the replacement flow if user only selected 1 item
      // and has one issue selected, we're making the assumption that the one selected issue
      // is the fit issue here
      const {allIds, byId} = state.selectedItemsToExchange
      if (allIds.length === 1 && byId[selectedItemId].issues.length === 1) {
        return {
          ...stateWithTailoringFlag,
          currentStep: ReplacementsSteps.tailoringEnd,
        }
      }

      // navigate back to summary step if we're in edit mode or if user reported additional non-fit issues for current item
      if (
        state.isEditing ||
        replacementsSelectors.currentItemHasNonFitIssues({replacements: state})
      ) {
        return {...stateWithTailoringFlag, currentStep: ReplacementsSteps.summary, isEditing: false}
      }

      return reduceCurrentStepAndItemIndex(stateWithTailoringFlag)
    }

    case SUBMIT_TAILORING_CONTINUE_EXCHANGE: {
      // navigate back to summary step if we're in edit mode or if user reported additional non-fit issues for current item
      if (
        state.isEditing ||
        replacementsSelectors.currentItemHasNonFitIssues({replacements: state})
      ) {
        return {
          ...state,
          currentStep: ReplacementsSteps.summary,
          isEditing: false,
        }
      }

      return reduceCurrentStepAndItemIndex(state)
    }

    case SUBMIT_SUMMARY: {
      return {
        currentStep: shouldNavigateToAddressForm(state)
          ? ReplacementsSteps.addressForm
          : ReplacementsSteps.justRight,
      }
    }

    case GET_SHIPPING_ADDRESS_FOR_EXCHANGE_PENDING:
      return {
        shippingInfo: {
          ...state.shippingInfo,
          isFetchingShippingAddress: true,
        },
      }

    case GET_SHIPPING_ADDRESS_FOR_EXCHANGE_FAILURE:
      return {
        shippingInfo: {
          ...state.shippingInfo,
          isFetchingShippingAddress: false,
        },
      }

    case GET_SHIPPING_ADDRESS_FOR_EXCHANGE_SUCCESS:
      return {
        shippingInfo: {
          ...state.shippingInfo,
          shippingAddress: formatShippingAddress(action.payload),
          isFetchingShippingAddress: false,
        },
      }

    case SUBMIT_EXCHANGE_REQUEST:
      return {
        shippingInfo: action.payload,
      }
    case SUBMIT_EXCHANGE_REQUEST_PENDING:
      return {
        submitExchangeRequest: {
          error: false,
          isPending: true,
        },
      }
    case SUBMIT_EXCHANGE_REQUEST_FAILURE:
      return {
        submitExchangeRequest: {
          error: true,
          isPending: false,
        },
      }
    case SUBMIT_EXCHANGE_REQUEST_SUCCESS:
      return {
        currentStep: ReplacementsSteps.confirmation,
        submitExchangeRequest: {
          error: false,
          isPending: false,
        },
      }
    case TOGGLE_COLLAR_EXTENDER_NEEDED:
      return merge({}, state, {
        selectedItemsToExchange: {
          byId: {
            [payload]: {
              issuesByType: {
                fit: {
                  isCollarExtenderNeeded:
                    !state.selectedItemsToExchange.byId[payload].issuesByType.fit
                      .isCollarExtenderNeeded,
                },
              },
            },
          },
        },
      })
    default:
      return null
  }
})
